import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { InputField, Modal, Select } from '@enamelai/design-system'
import type { SavedViewRow } from '@constants/Reports/presetsAutomation.constants'
import {
  getDefaultPeriodForLatestData,
  getPeriodToolbarMenuForLatestData,
  isPeriodIdAllowedForLatestData,
  REPORTS_TOOLBAR_MENUS,
  type ReportsToolbarMenuEntry,
} from '@constants/Reports/reportFilterToolbarMenus.constants'

export type EditSavedViewValues = {
  name: string
  toolbarSelection: Record<string, string>
}

type EditSavedViewModalProps = {
  show: boolean
  onHide: () => void
  savedView: SavedViewRow | null
  onSave: (values: EditSavedViewValues) => void
}

type MenuItem = Extract<ReportsToolbarMenuEntry, { type: 'item' }>

function menuItems(entries: ReportsToolbarMenuEntry[]): MenuItem[] {
  return entries.filter((entry): entry is MenuItem => entry.type === 'item')
}

function EditSavedViewModal({
  show,
  onHide,
  savedView,
  onSave,
}: EditSavedViewModalProps): React.ReactElement {
  const [name, setName] = useState('')
  const [latestData, setLatestData] = useState('historical')
  const [period, setPeriod] = useState('last-90-days')
  const [provider, setProvider] = useState('all-providers')

  useEffect(() => {
    if (!show || !savedView) return
    const selection = {
      ...savedView.preset.toolbarSelection,
      ...(savedView.bundleView?.toolbarSelection ?? {}),
    }
    const nextLatest = selection['period-category'] ?? 'historical'
    const nextPeriod = selection['period-preset'] ?? ''
    setName(savedView.name)
    setLatestData(nextLatest)
    setPeriod(
      isPeriodIdAllowedForLatestData(nextPeriod, nextLatest)
        ? nextPeriod
        : getDefaultPeriodForLatestData(nextLatest),
    )
    setProvider(selection['provider-filter'] ?? 'all-providers')
  }, [show, savedView])

  const latestDataOptions = useMemo(
    () =>
      menuItems(REPORTS_TOOLBAR_MENUS['period-category'] ?? [])
        .filter(
          (item) =>
            item.id === 'latest-data' ||
            item.id === 'historical' ||
            item.id === 'snapshot',
        )
        .map((item) => ({ value: item.id, label: item.label })),
    [],
  )
  const periodOptions = useMemo(
    () =>
      menuItems(getPeriodToolbarMenuForLatestData(latestData)).map((item) => ({
        value: item.id,
        label: item.label,
      })),
    [latestData],
  )
  const providerOptions = useMemo(
    () =>
      menuItems(REPORTS_TOOLBAR_MENUS['provider-filter'] ?? []).map((item) => ({
        value: item.id,
        label: item.label,
      })),
    [],
  )

  const handleLatestDataChange = useCallback((next: string) => {
    setLatestData(next)
    setPeriod((previous) =>
      isPeriodIdAllowedForLatestData(previous, next)
        ? previous
        : getDefaultPeriodForLatestData(next),
    )
  }, [])

  const handleSave = useCallback(() => {
    if (!savedView) return
    const trimmed = name.trim()
    if (!trimmed) return
    onSave({
      name: trimmed,
      toolbarSelection: {
        ...savedView.preset.toolbarSelection,
        ...(savedView.bundleView?.toolbarSelection ?? {}),
        'period-category': latestData,
        'period-preset': period,
        'provider-filter': provider,
      },
    })
    onHide()
  }, [latestData, name, onHide, onSave, period, provider, savedView])

  const nameInvalid = name.trim().length === 0

  return (
    <Modal
      open={show}
      onClose={onHide}
      title="Edit saved view"
      size="md"
      className="reports-edit-saved-view-modal"
      bodyClassName="reports-edit-saved-view-modal__body"
      secondaryLabel="Cancel"
      primaryLabel="Save changes"
      primaryDisabled={nameInvalid || !savedView}
      onSecondaryClick={onHide}
      onPrimaryClick={handleSave}
    >
      <div className="reports-edit-saved-view-modal__field">
        <InputField
          id="edit-saved-view-name"
          label="Report name"
          variant="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={nameInvalid}
          helperText={nameInvalid ? 'Report name is required.' : undefined}
          fullWidth
          clearable={false}
        />
      </div>

      <div className="reports-edit-saved-view-modal__field">
        <span className="reports-edit-saved-view-modal__label">
          Report period
        </span>
        <div className="reports-edit-saved-view-modal__row">
          <Select
            options={latestDataOptions}
            value={latestData}
            onChange={(value) => handleLatestDataChange(String(value))}
            className="reports-edit-saved-view-modal__dropdown"
          />
          <Select
            options={periodOptions}
            value={period}
            onChange={(value) => setPeriod(String(value))}
            className="reports-edit-saved-view-modal__dropdown"
          />
        </div>
      </div>

      <div className="reports-edit-saved-view-modal__field">
        <Select
          label="Report data"
          options={providerOptions}
          value={provider}
          onChange={(value) => setProvider(String(value))}
          className="reports-edit-saved-view-modal__dropdown"
        />
      </div>
    </Modal>
  )
}

export default EditSavedViewModal
